import React, { Suspense, useEffect, useRef, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import { ParallaxProvider } from "react-scroll-parallax";
import { Route, Routes, useLocation, BrowserRouter } from "react-router-dom";
import theme from "./theme";
import logoImg from "../src/residenzaStoricaAssets/1811white.png";
import "./App.css";
import { useMediaQuery } from "@uidotdev/usehooks";
import Navbar from "./components/Navbar/Navbar";
import MobileScrollLayout from "./components/Layout/MobileScrollLayout";
import CustomMobileLanding from "./pages/YogaSpace/Components/CustomMobileLanding";
import Footer from "./pages/Footer/Footer";
import ScrollToTop from "./utils/hooks/ScrollToTop";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import ProtectedRoute from "./utils/config/Login/ProtectedRoute";
import { AuthProvider } from "./utils/config/Login/AuthContext";
import useRedirectOnBack from "./utils/hooks/useRedirectOnBack";
import { Box } from "@mui/system";
import RoomSingle from "./pages/Rooms/RoomSingle";
import RoomMenu from "./pages/Rooms/RoomMenu";
import AdminPage from "./pages/Admin/AdminPage";
import Checkout from "./pages/Checkout/Checkout";
const HomePage = React.lazy(() => import("./pages/YogaSpace/HomePage"));
const Rooms = React.lazy(() => import("./pages/Rooms/Rooms"));
const RoomsPage = React.lazy(() => import("./pages/Rooms/RoomsPage"));
const BookRoom = React.lazy(() => import("./pages/Admin/BookRoom"));
const ReachUsMap = React.lazy(() => import("./pages/ReachUs/ReachUsMap"));
const ReachUs = React.lazy(() => import("./pages/ReachUs/ReachUs"));
const PaymentResult = React.lazy(
  () => import("./pages/Checkout/PaymentResult")
);
const Login = React.lazy(() => import("./utils/config/Login/Login"));
// Aggiungi questa funzione per il wrapping riutilizzabile
// @ts-ignore
function LazyRoute({ component }) {
  return <Suspense fallback={<LoadingScreen />}>{component}</Suspense>;
}

function AnimatedRoutes() {
  useRedirectOnBack();
  const location = useLocation();
  return (
    <TransitionGroup>
      <CSSTransition key={location.key} timeout={800} classNames="slide">
        <div className="page-wrapper">
          <Routes location={location}>
            <Route path="/" element={<HomePageWrapper />} />
            <Route
              path="/booking"
              element={<LazyRoute component={<BookingWrapper />} />}
            />
            <Route path="/rooms/:id" element={<RoomSingleWrapper />} />
            <Route
              path="/explorerooms"
              element={<LazyRoute component={<ExploreRoomsWrapper />} />}
            />
            <Route
              path="/Rooms"
              element={<LazyRoute component={<RoomsPageWrapper />} />}
            />
            <Route
              path="/location"
              element={<LazyRoute component={<LocationWrapper />} />}
            />
            <Route
              path="/payment-result"
              element={<LazyRoute component={<PaymentResult />} />}
            />
            <Route
              path="/raggiungici"
              element={<LazyRoute component={<ReachUsMapWrapper />} />}
            />
            <Route
              path="/checkout"
              element={<LazyRoute component={<Checkout />} />}
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/login"
              element={<LazyRoute component={<LoginPageWrapper />} />}
            />
          </Routes>
        </div>
      </CSSTransition>
    </TransitionGroup>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <ParallaxProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AnimatedRoutes />
          </BrowserRouter>
        </AuthProvider>
      </ParallaxProvider>
    </ThemeProvider>
  );
}

function LoadingScreen() {
  const isTabletOrMobile = useMediaQuery("(max-width: 768px)");
  return (
    <>
      {isTabletOrMobile ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            width: "100vw",
            backgroundColor: "#485e6c",
          }}
        >
          <Box
            style={{
              width: "100%",
              height: "100vh",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              alt="Stemma 1811"
              src={logoImg}
              width={isTabletOrMobile ? "75%" : "30%"}
            />
            <CircularProgress
              sx={{
                color: "white",
              }}
            />
          </Box>{" "}
        </div>
      ) : (
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            zIndex: 1000,
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "#485e6c",
          }}
        >
          <Box
            style={{
              width: "100%",
              height: "100vh",
              display: isTabletOrMobile ? "none" : "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              alt="Stemma 1811"
              src={logoImg}
              width={isTabletOrMobile ? "95%" : "30%"}
            />
            <CircularProgress
              sx={{
                color: "white",
              }}
            />
          </Box>{" "}
        </Box>
      )}
    </>
  );
}

function HomePageWrapper() {
  const isTabletOrMobile = useMediaQuery("(max-width: 768px)");
  const containerRef = useRef(null); // Riferimento per il box che contiene CustomMobileLanding e RoomMenu
  const [containerHeight, setContainerHeight] = useState(0); // Stato per l'altezza dinamica
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const hasLoadedBefore = localStorage.getItem("hasLoaded");

    // Se non è ancora stato caricato, attiva il timer e imposta il flag in localStorage
    if (!hasLoadedBefore) {
      const timer = setTimeout(() => {
        setLoading(false);
        localStorage.setItem("hasLoaded", "true"); // Memorizza il flag
      }, 6000); // Nascondi il loading dopo 4 secondi

      return () => clearTimeout(timer); // Cleanup del timer su unmount
    } else {
      setLoading(false); // Se ha già caricato prima, disabilita direttamente il loading
    }
  }, []);
  useEffect(() => {
    const updateContainerHeight = () => {
      if (containerRef.current) {
        // @ts-ignore
        setContainerHeight(containerRef.current.offsetHeight); // Ottieni l'altezza dell'elemento
      }
    };

    updateContainerHeight(); // Calcola l'altezza iniziale

    window.addEventListener("resize", updateContainerHeight); // Aggiorna l'altezza su resize

    return () => {
      window.removeEventListener("resize", updateContainerHeight); // Rimuovi listener su unmount
    };
  }, []);
  return isTabletOrMobile ? (
    <>{loading ? <LoadingScreen /> : <MobileScrollLayout />}</>
  ) : (
    <>
      {loading ? (
        <LoadingScreen />
      ) : (
        <>
          <Box
            ref={containerRef} // Riferimento al container
            width="100%"
            zIndex={9999999}
            position="fixed" // Cambiato da "absolute" a "relative"
            top={0}
          >
            <Box>
              {" "}
              <Navbar />
            </Box>
          </Box>
          <Box mt={`${containerHeight}px`}>
            <HomePage />;
          </Box>
        </>
      )}
    </>
  );
}

function BookingWrapper() {
  const isMobile = useMediaQuery("(max-width: 768px)"); // Definisci il breakpoint per tablet e mobile
  return (
    <>
      {isMobile ? <CustomMobileLanding isRooms /> : <Navbar />}
      <Box sx={{ backgroundColor: "#f4eeee" }}>
        <BookRoom />
      </Box>
      <Footer />
    </>
  );
}

function ExploreRoomsWrapper() {
  const isMobile = useMediaQuery("(max-width: 768px)"); // Definisci il breakpoint per tablet e mobile

  return (
    <>
      {isMobile ? <CustomMobileLanding isRooms /> : <Navbar />}
      <Rooms />
      <Footer />
    </>
  );
}
function LoginPageWrapper() {
  return (
    <>
      <Login />
    </>
  );
}

function RoomsPageWrapper() {
  const isMobile = useMediaQuery("(max-width: 768px)"); // Definisci il breakpoint per tablet e mobile

  return (
    <>
      {isMobile ? <CustomMobileLanding isRooms /> : <Navbar />}
      <RoomsPage />
    </>
  );
}

function RoomSingleWrapper() {
  const isMobile = useMediaQuery("(max-width: 768px)"); // Breakpoint per tablet e mobile
  const containerRef = useRef(null); // Riferimento per il box che contiene CustomMobileLanding e RoomMenu
  const [containerHeight, setContainerHeight] = useState(0); // Stato per l'altezza dinamica

  useEffect(() => {
    const updateContainerHeight = () => {
      if (containerRef.current) {
        // @ts-ignore
        setContainerHeight(containerRef.current.offsetHeight); // Ottieni l'altezza dell'elemento
      }
    };

    updateContainerHeight(); // Calcola l'altezza iniziale

    window.addEventListener("resize", updateContainerHeight); // Aggiorna l'altezza su resize

    return () => {
      window.removeEventListener("resize", updateContainerHeight); // Rimuovi listener su unmount
    };
  }, []);

  return (
    <>
      {/* Box che contiene CustomMobileLanding e RoomMenu */}
      <Box
        ref={containerRef} // Riferimento al container
        width="100%"
        zIndex={9999999}
        position={isMobile ? "fixed" : undefined} // Cambiato da "absolute" a "relative"
        top={0}
      >
        <Box>{isMobile ? <CustomMobileLanding isRooms /> : <Navbar />}</Box>
        <RoomMenu />
      </Box>

      {/* Applica un margin-top dinamico a RoomSingle in base all'altezza del container */}
      <Box mt={`${containerHeight}px`}>
        <RoomSingle />
      </Box>
    </>
  );
}

function LocationWrapper() {
  const isMobile = useMediaQuery("(max-width: 768px)"); // Definisci il breakpoint per tablet e mobile

  return (
    <>
      {isMobile ? <CustomMobileLanding isRooms /> : <Navbar />}
      <ReachUs />
      <Footer />
    </>
  );
}

function ReachUsMapWrapper() {
  const isMobile = useMediaQuery("(max-width: 768px)"); // Definisci il breakpoint per tablet e mobile

  return (
    <>
      {isMobile ? <CustomMobileLanding isRooms /> : <Navbar />}
      <ReachUsMap />
      <Footer />
    </>
  );
}

export default App;
